import { formatMessage, FormattedHTMLMessage } from '@99designs/common/i18n';
import { SquarespaceApiBrief } from '__generated__/SquarespaceApiBrief';
import { SquarespaceApiIndustry } from '__generated__/types';
import { handlePromise } from 'common/utils/Bugsnag';
import SquarespaceDesignerConfirmationModal from 'projects/components/SquarespaceDesignerConfirmationModal';
import SquarespaceDesignerResults from 'projects/components/SquarespaceDesignerResults';
import SquarespaceDesignerSelection from 'projects/components/SquarespaceDesignerSelection';
import SquarespaceFooter from 'projects/components/SquarespaceFooter';
import { Color, MAX_DESIGNERS } from 'projects/consts';
import { trackRemovedDesignerEvent } from 'projects/events';
import RemoveStructuredBriefDesigners from 'projects/mutations/RemoveStructuredBriefDesigners';
import * as React from 'react';
import { intlShape } from 'react-intl';

export interface Props {
    brief: SquarespaceApiBrief;
    launch(): Promise<void>;
}

interface State {
    showConfirmationModal: boolean;
}

export default class SquarespaceDesignersPage extends React.Component<Props, State> {
    state = {
        showConfirmationModal: false,
    };

    static contextTypes = {
        intl: intlShape.isRequired,
    };

    getIndustry(industry: SquarespaceApiIndustry): string {
        switch (industry) {
            case SquarespaceApiIndustry.ACCOUNTING_AND_FINANCIAL:
                return __('Accounting & financial');
            case SquarespaceApiIndustry.ART_AND_DESIGN:
                return __('Art & design');
            case SquarespaceApiIndustry.BEAUTY_AND_PERSONAL_CARE:
                return __('Beauty & personal care');
            case SquarespaceApiIndustry.COMMUNITY_AND_NONPROFIT:
                return __('Community & nonprofit');
            case SquarespaceApiIndustry.ENTERTAINMENT_AND_MEDIA:
                return __('Entertainment & media');
            case SquarespaceApiIndustry.FASHION:
                return __('Fashion');
            case SquarespaceApiIndustry.FOOD_AND_DRINK:
                return __('Food & drink');
            case SquarespaceApiIndustry.HEALTH_AND_FITNESS:
                return __('Health & fitness');
            case SquarespaceApiIndustry.HOME_AND_LIVING:
                return __('Home & living');
            case SquarespaceApiIndustry.INTERIOR_DESIGN:
                return __('Interior design');
            case SquarespaceApiIndustry.LEGAL_SERVICES_AND_ATTORNEYS:
                return __('Legal services & attorneys');
            case SquarespaceApiIndustry.MARKETING_AND_ADVERTISING:
                return __('Marketing & advertising');
            case SquarespaceApiIndustry.MUSIC:
                return __('Music');
            case SquarespaceApiIndustry.PHOTOGRAPHY:
                return __('Photography');
            case SquarespaceApiIndustry.REAL_ESTATE:
                return __('Real estate');
            case SquarespaceApiIndustry.RETAIL_RESTAURANT_AND_BAR:
                return __('Retail, restaurant & bar');
            case SquarespaceApiIndustry.TECHNOLOGY:
                return __('Technology');
            case SquarespaceApiIndustry.TRAVEL_AND_LIFESTYLE:
                return __('Travel & lifestyle');
            case SquarespaceApiIndustry.WEDDING:
                return __('Wedding');
        }
    }

    render() {
        const { brief } = this.props;
        const { industry } = this.props.brief.fields as { industry: SquarespaceApiIndustry };

        return (
            <>
                <div className="content-setting content-setting--full">
                    <div className="paragraph paragraph--centered paragraph--double-margin">
                        <FormattedHTMLMessage
                            message={__(
                                'Your designers have been carefully chosen based on the category and industry: <b>Squarespace / {industry}</b><br />Please select up to five designers to invite. You’ll receive custom quote(s) which you can approve to kick off your exciting website project!'
                            )}
                            values={{
                                industry: industry ? this.getIndustry(industry) : '-',
                            }}
                        />
                    </div>
                    <SquarespaceDesignerResults brief={brief} />
                </div>
                <SquarespaceDesignerConfirmationModal
                    brief={brief}
                    onDismiss={() => this.setState({ showConfirmationModal: false })}
                    onConfirm={() => this.props.launch()}
                    disabled={false}
                    showing={this.state.showConfirmationModal}
                />
                <SquarespaceFooter
                    nextAction={() => this.setState({ showConfirmationModal: true })}
                    nextActionLabel={__('Next')}
                    disabled={brief.designers.length == 0}
                >
                    <RemoveStructuredBriefDesigners>
                        {removeDesigners => (
                            <SquarespaceDesignerSelection
                                showTooltips
                                designers={brief.designers}
                                onRemoveDesigner={designerId => {
                                    trackRemovedDesignerEvent({
                                        id: brief.id,
                                        designer_id: designerId,
                                    });
                                    handlePromise(
                                        removeDesigners({
                                            id: brief.id,
                                            designerIds: [designerId],
                                        }),
                                        'SquarespaceDesignersPage RemoveDesigners'
                                    );
                                }}
                                color={Color.squarespaceBlack}
                                selectDesignersCopy={{
                                    heading: __('Selected designers'),
                                    message: formatMessage(
                                        __(
                                            "Choose up to {maxDesigners} designers to invite. When ready, click 'Next' to review and request quotes."
                                        ),
                                        {
                                            maxDesigners: MAX_DESIGNERS,
                                        }
                                    ),
                                    storageKey:
                                        'squarespace_designer_selection_designer_placeholder_tooltip',
                                }}
                                confirmDesignersCopy={{
                                    heading: __('You’re done!'),
                                    message: __(
                                        'Simply confirm your chosen designers to request quote(s) and get underway.'
                                    ),
                                    storageKey:
                                        'squarespace_designer_selection_max_designers_tooltip',
                                }}
                            />
                        )}
                    </RemoveStructuredBriefDesigners>
                </SquarespaceFooter>
            </>
        );
    }
}
